import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AppShell } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { formatMoney } from "@/lib/constants";
import { toast } from "sonner";
import { Wallet as WalletIcon, ArrowDownLeft, ArrowUpRight } from "lucide-react";

const LABELS = { escrow_funded: "Paid into escrow", refund: "Refund", earning: "Earning released", withdrawal: "Withdrawal", withdrawal_reversed: "Withdrawal returned" };

function Stat({ label, value, testid }) {
  return (
    <div className="rounded-2xl border border-border bg-surface p-5" data-testid={testid}>
      <p className="text-xs text-muted-foreground uppercase tracking-widest">{label}</p>
      <p className="font-heading font-black text-2xl mt-1">{value}</p>
    </div>
  );
}

function WithdrawDialog({ currency, available, onDone }) {
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const [details, setDetails] = useState("");
  const submit = async () => {
    try {
      await api.post("/wallet/withdraw", { amount: Number(amount), currency, payout_details: details });
      toast.success("Withdrawal requested. It will be processed within 3–5 business days.");
      setOpen(false); setAmount(""); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not request withdrawal."); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button disabled={available <= 0} data-testid={`withdraw-btn-${currency}`} className="rounded-full bg-neon-pink text-white hover:bg-neon-pink/90 font-semibold">Withdraw {currency}</Button>
      </DialogTrigger>
      <DialogContent className="bg-surface border-border max-w-md">
        <DialogHeader><DialogTitle className="font-heading text-2xl">Request withdrawal</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">Available: {formatMoney(available, currency)}</p>
          <div><Label className="mb-2 block">Amount ({currency})</Label><Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="withdraw-amount-input" className="bg-background border-border h-11" /></div>
          <div><Label className="mb-2 block">UPI ID or bank account (A/C no + IFSC)</Label><Input value={details} onChange={(e) => setDetails(e.target.value)} data-testid="withdraw-details-input" className="bg-background border-border h-11" /></div>
          <Button onClick={submit} disabled={!(Number(amount) > 0) || !details.trim()} data-testid="withdraw-submit-btn" className="w-full rounded-full bg-neon-pink text-white font-bold">Request withdrawal</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function WalletPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const isPromoter = user?.role === "promoter";
  const load = () => api.get("/wallet").then((r) => setData(r.data)).catch(() => toast.error("Could not load wallet."));
  useEffect(() => { load(); }, []);

  const balances = Object.entries(data?.balances || {});
  return (
    <AppShell>
      <div className="max-w-5xl mx-auto" data-testid="wallet-page">
        <h1 className="font-heading font-black text-4xl tracking-tight mb-2 flex items-center gap-3"><WalletIcon className="h-8 w-8 text-neon-pink" /> Wallet</h1>
        <p className="text-muted-foreground mb-8">{isPromoter ? "Money you've paid into escrow, released to creators, and refunds." : `Your earnings after the ${Math.round((data?.fee_rate || 0.2) * 100)}% platform fee.`}</p>
        {!data ? <p className="text-muted-foreground">Loading…</p> : balances.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-border bg-surface p-10 text-center text-muted-foreground" data-testid="wallet-empty">No wallet activity yet.</div>
        ) : balances.map(([cur, b]) => (
          <div key={cur} className="mb-6">
            <div className="grid sm:grid-cols-3 gap-4">
              {isPromoter ? (<>
                <Stat label={`In escrow · ${cur}`} value={formatMoney(b.in_escrow, cur)} testid={`stat-escrow-${cur}`} />
                <Stat label="Released to creators" value={formatMoney(b.total_spent, cur)} testid={`stat-spent-${cur}`} />
                <Stat label="Refunded" value={formatMoney(b.refunded, cur)} testid={`stat-refunded-${cur}`} />
              </>) : (<>
                <Stat label={`Available · ${cur}`} value={formatMoney(b.available, cur)} testid={`stat-available-${cur}`} />
                <Stat label="Pending in escrow" value={formatMoney(b.in_escrow, cur)} testid={`stat-pending-${cur}`} />
                <Stat label="Total earned" value={formatMoney(b.total_earned, cur)} testid={`stat-earned-${cur}`} />
              </>)}
            </div>
            {!isPromoter && <div className="mt-3"><WithdrawDialog currency={cur} available={b.available} onDone={load} /></div>}
          </div>
        ))}
        {data?.transactions?.length > 0 && (
          <div className="mt-10">
            <h2 className="font-heading font-bold text-lg mb-4">Transaction history</h2>
            <div className="rounded-2xl border border-border bg-surface divide-y divide-border" data-testid="wallet-transactions">
              {data.transactions.map((t) => (
                <div key={t.txn_id} className="flex items-center justify-between gap-4 p-4" data-testid={`txn-${t.txn_id}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    {t.amount >= 0 ? <ArrowDownLeft className="h-4 w-4 text-neon-lime shrink-0" /> : <ArrowUpRight className="h-4 w-4 text-neon-pink shrink-0" />}
                    <div className="min-w-0">
                      <p className="font-semibold text-sm">{LABELS[t.type] || t.type}{t.campaign_title ? ` · ${t.campaign_title}` : ""}</p>
                      <p className="text-xs text-muted-foreground truncate">{new Date(t.created_at).toLocaleString()} {t.note ? `· ${t.note}` : ""}</p>
                    </div>
                  </div>
                  <p className={`font-heading font-bold shrink-0 ${t.amount >= 0 ? "text-neon-lime" : ""}`}>{t.amount >= 0 ? "+" : "−"}{formatMoney(Math.abs(t.amount), t.currency)}</p>
                </div>
              ))}
            </div>
          </div>
        )}
        {data?.withdrawals?.length > 0 && (
          <div className="mt-8">
            <h2 className="font-heading font-bold text-lg mb-4">Withdrawal requests</h2>
            {data.withdrawals.map((w) => (
              <p key={w.withdrawal_id} className="text-sm py-1" data-testid={`withdrawal-${w.withdrawal_id}`}>{formatMoney(w.amount, w.currency)} → {w.payout_details} · <span className="capitalize">{w.status}</span></p>
            ))}
          </div>
        )}
      </div>
    </AppShell>
  );
}
